import { useEffect, useState, useRef } from "react";
import { Twitter, Linkedin, Mail, ArrowUpRight, Rocket } from "lucide-react";
import { siteConfig, socialLinks } from "../data/mock";
import { Button } from "./ui/button";
import PitchForm from "./PitchForm";

const iconMap = {
  Twitter: Twitter,
  Linkedin: Linkedin,
};

const ContactSection = () => {
  const [visible, setVisible] = useState(false);
  const [isPitchFormOpen, setIsPitchFormOpen] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" className="py-32 px-6" ref={sectionRef}>
      <div className="max-w-4xl mx-auto text-center">
        {/* CTA Tag */}
        <div
          className={`transform transition-all duration-700 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <span className="inline-block font-mono text-xs tracking-[0.3em] text-[#00F0FF] border border-[#00F0FF]/30 px-4 py-2 rounded-sm mb-8">
            PITCH US
          </span>
        </div>

        {/* Main Headline */}
        <h2
          className={`font-mono text-3xl md:text-4xl lg:text-5xl text-[#F3F4F6] tracking-wide transform transition-all duration-700 delay-100 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          Building in AI?
        </h2>

        {/* Subtitle */}
        <p
          className={`font-sans text-lg text-[#6B7280] mt-4 mb-8 transform transition-all duration-700 delay-200 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          We want to hear from you. Show us what you&apos;re building.
        </p>

        {/* CTA Button */}
        <div
          className={`transform transition-all duration-700 delay-300 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <Button
            size="lg"
            onClick={() => setIsPitchFormOpen(true)}
            className="bg-[#00F0FF] text-[#0a0a0f] hover:bg-[#00F0FF]/90 font-mono text-sm tracking-wider px-8 py-6 rounded-lg group"
          >
            <Rocket className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" />
            SUBMIT YOUR PITCH
          </Button>
        </div>

        {/* Divider */}
        <div
          className={`w-24 h-[1px] bg-gradient-to-r from-transparent via-white/20 to-transparent mx-auto my-12 transform transition-all duration-700 delay-400 ${
            visible ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* Website Link */}
        <a
          href={`https://${siteConfig.website}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`group inline-flex items-center gap-2 transform transition-all duration-700 delay-500 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
        >
          <span className="font-mono text-lg text-[#6B7280] group-hover:text-[#00F0FF] transition-colors duration-300">
            {siteConfig.website}
          </span>
          <ArrowUpRight className="w-4 h-4 text-[#6B7280] group-hover:text-[#00F0FF] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
        </a>

        {/* Email */}
        <a
          href={`mailto:${siteConfig.email}`}
          className={`flex items-center justify-center gap-2 font-mono text-sm text-[#6B7280] hover:text-[#00F0FF] mt-4 transition-colors duration-300 transform ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
          style={{ transitionDelay: "600ms" }}
        >
          <Mail className="w-4 h-4" />
          {siteConfig.email}
        </a>

        {/* Social Links */}
        <div
          className={`flex items-center justify-center gap-4 mt-8 transform transition-all duration-700 ${
            visible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
          }`}
          style={{ transitionDelay: "700ms" }}
        >
          {socialLinks.map((link) => {
            const Icon = iconMap[link.icon];
            return (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#6B7280] hover:text-[#00F0FF] hover:border-[#00F0FF]/30 hover:bg-[#00F0FF]/5 transition-all duration-300"
                title={link.name}
              >
                <Icon className="w-5 h-5" />
              </a>
            );
          })}  
        </div>
      </div>

      {/* Pitch Form Modal */}
      <PitchForm isOpen={isPitchFormOpen} onClose={() => setIsPitchFormOpen(false)} />
    </section>
  );
};

export default ContactSection;
